import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';

function DiscountingPage({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [pendingEditRecord, setPendingEditRecord] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [availableForwards, setAvailableForwards] = useState([]);
  const [availablePcfc, setAvailablePcfc] = useState([]);
  const [pcfcAllocations, setPcfcAllocations] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [forwardAllocations, setForwardAllocations] = useState([]);
  const [form, setForm] = useState({
    customerCode: '', invoiceId: '', discountingDate: '',
    bank: '', fcAmountDiscounted: '', discountingType: 'INR',
    spotFc: '', spotRate: '', pcfcFc: '', pcfcRate: '',
    drawdownRate: '', loanDueDate: '', notes: ''
  });

  useEffect(() => {
    loadRecords();
    loadCustomers();
    loadBanks();
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${CONFIG.API}/api/bills/discounting`, {
        headers: { authorization: `Bearer ${token}` }
      });
      setRecords(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/discounting/customers`,
        { headers: { authorization: `Bearer ${token}` } }
      );
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadBanks = async () => {
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/banks`,
        { headers: { authorization: `Bearer ${token}` } }
      );
      setBanks(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadOpenInvoices = async (customerCode) => {
    if (!customerCode) { setOpenInvoices([]); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/discounting/open-invoices/${customerCode}`,
        { headers: { authorization: `Bearer ${token}` } }
      );
      setOpenInvoices(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAvailableForwards = async (invoiceId, discountingType, discountingDate) => {
    if (!invoiceId) { setAvailableForwards([]); return; }
    try {
      let url = '';
      if (discountingType === 'INR') {
        url = `${CONFIG.API}/api/bills/discounting/available-forwards/${invoiceId}`;
      } else {
        if (!discountingDate) { setAvailableForwards([]); return; }
        url = `${CONFIG.API}/api/bills/discounting/available-forwards-fc/${invoiceId}/${discountingDate}`;
      }
      const res = await axios.get(url, {
        headers: { authorization: `Bearer ${token}` }
      });
      setAvailableForwards(res.data);
      setForwardAllocations([]);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAvailablePcfc = async (invoiceId, bank) => {
    if (!invoiceId || !bank) { setAvailablePcfc([]); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/discounting/available-pcfc/${invoiceId}`,
        {
          params: { bank },
          headers: { authorization: `Bearer ${token}` }
        }
      );
      setAvailablePcfc(res.data);
      setPcfcAllocations([]);
    } catch (err) {
      console.error(err);
      setAvailablePcfc([]);
    }
  };

  const handleChange = (e) => {
    const updated = { ...form, [e.target.name]: e.target.value };
    setForm(updated);

    if (e.target.name === 'customerCode') {
      loadOpenInvoices(e.target.value);
      setForm({ ...updated, invoiceId: '' });
      setAvailableForwards([]);
      setForwardAllocations([]);
      setAvailablePcfc([]);
      setPcfcAllocations([]);
    }

    if (e.target.name === 'invoiceId') {
      loadAvailableForwards(
        e.target.value,
        updated.discountingType,
        updated.discountingDate
      );
      if (updated.discountingType === 'FC') {
        loadAvailablePcfc(e.target.value, updated.bank);
      }
    }

    if (e.target.name === 'discountingType') {
      setForm({ ...updated, spotFc: '', spotRate: '' });
      setAvailableForwards([]);
      setForwardAllocations([]);
      setAvailablePcfc([]);
      setPcfcAllocations([]);
      if (e.target.value === 'FC' && updated.invoiceId && updated.discountingDate) {
        loadAvailableForwards(updated.invoiceId, 'FC', updated.discountingDate);
      }
      if (e.target.value === 'FC' && updated.invoiceId && updated.bank) {
        loadAvailablePcfc(updated.invoiceId, updated.bank);
      }
      if (e.target.value === 'INR' && updated.invoiceId) {
        loadAvailableForwards(updated.invoiceId, 'INR', '');
      }
    }

    if (e.target.name === 'bank' &&
        updated.discountingType === 'FC' && updated.invoiceId) {
      loadAvailablePcfc(updated.invoiceId, e.target.value);
    }

    if (e.target.name === 'discountingDate' &&
        updated.discountingType === 'FC' && updated.invoiceId) {
      loadAvailableForwards(updated.invoiceId, 'FC', e.target.value);
    }
  };

  const handleForwardAllocationChange = (forwardId, value) => {
    const existing = forwardAllocations.find(a => a.forwardId === forwardId);
    if (existing) {
      setForwardAllocations(forwardAllocations.map(a =>
        a.forwardId === forwardId ? { ...a, fcAllocated: value } : a
      ));
    } else {
      const fwd = availableForwards.find(f => f.forwardId === forwardId);
      setForwardAllocations([...forwardAllocations, {
        forwardId,
        fcAllocated: value,
        forwardRate: fwd?.forwardRate || 0
      }]);
    }
  };

  const handlePcfcAllocationChange = (pcfcId, value) => {
    const existing = pcfcAllocations.find(a => a.pcfcId === pcfcId);
    if (existing) {
      setPcfcAllocations(pcfcAllocations.map(a =>
        a.pcfcId === pcfcId ? { ...a, fcAllocated: value } : a
      ));
    } else {
      setPcfcAllocations([...pcfcAllocations, { pcfcId, fcAllocated: value }]);
    }
  };

  const resetForm = () => {
    setForm({
      customerCode: '', invoiceId: '', discountingDate: '',
      bank: '', fcAmountDiscounted: '', discountingType: 'INR',
      spotFc: '', spotRate: '', pcfcFc: '', pcfcRate: '',
      drawdownRate: '', loanDueDate: '', notes: ''
    });
    setForwardAllocations([]);
    setAvailableForwards([]);
    setAvailablePcfc([]);
    setPcfcAllocations([]);
    setOpenInvoices([]);
    setEditingRecord(null);
    setShowForm(false);
  };

  const handleEditClick = (record) => {
    setPendingEditRecord(record);
    setShowEditWarning(true);
  };

  const handleEditConfirm = async () => {
    const r = pendingEditRecord;
    setShowEditWarning(false);
    setPendingEditRecord(null);
    const auth = { headers: { authorization: `Bearer ${token}` } };
    const ddate = (r.discountingDate || '').slice(0, 10);
    try {
      // open invoices for the customer; make sure THIS invoice is present with
      // the right available balance (its own discounted amount added back)
      let invs = [];
      try {
        const oi = await axios.get(
          `${CONFIG.API}/api/bills/discounting/open-invoices/${r.customerCode}`, auth);
        invs = oi.data;
      } catch (e) { /* ignore */ }
      const addBack = parseFloat(r.fcAmountDiscounted || 0);
      const exInv = invs.find(i => String(i.invoiceId) === String(r.invoiceId));
      if (exInv) exInv.fcOpen = parseFloat(exInv.fcOpen) + addBack;
      else invs.push({
        invoiceId: r.invoiceId, invoiceNo: r.invoiceNo,
        dueDate: r.dueDate, fcOpen: addBack, currency: r.currency
      });
      setOpenInvoices(invs);

      // available forwards
      let fwds = [];
      try {
        const url = r.discountingType === 'INR'
          ? `${CONFIG.API}/api/bills/discounting/available-forwards/${r.invoiceId}`
          : `${CONFIG.API}/api/bills/discounting/available-forwards-fc/${r.invoiceId}/${ddate}`;
        const f = await axios.get(url, auth);
        fwds = f.data;
      } catch (e) { /* ignore */ }

      // available PCFC (FC type)
      let pcfc = [];
      if (r.discountingType === 'FC' && r.bank) {
        try {
          const p = await axios.get(
            `${CONFIG.API}/api/bills/discounting/available-pcfc/${r.invoiceId}`,
            { ...auth, params: { bank: r.bank } });
          pcfc = p.data;
        } catch (e) { /* ignore */ }
      }

      // detail — exact forwards/PCFC this discounting used
      const det = await axios.get(
        `${CONFIG.API}/api/bills/discounting/${r.discountingId}/detail`, auth);
      const detFwd  = det.data.forwardAllocations || [];
      const detPcfc = det.data.pcfcAllocations || [];

      // add this record's own allocations back into availability
      detFwd.forEach(d => {
        const ex = fwds.find(f => f.forwardId === d.forwardId);
        if (ex) ex.fcAvailable = parseFloat(ex.fcAvailable) + parseFloat(d.fcAllocated);
        else fwds.push({
          forwardId: d.forwardId, dealNo: d.dealNo, bank: d.bank,
          forwardRate: d.forwardRate, fcAvailable: parseFloat(d.fcAllocated),
          windowStartDate: d.windowStartDate, windowEndDate: d.windowEndDate
        });
      });
      detPcfc.forEach(d => {
        const ex = pcfc.find(p => p.pcfcId === d.pcfcId);
        if (ex) ex.fcAvailable = parseFloat(ex.fcAvailable) + parseFloat(d.fcAllocated);
        else pcfc.push({
          pcfcId: d.pcfcId, pcfcRef: d.pcfcRef, pcfcDate: d.pcfcDate,
          drawdownRate: d.drawdownRate, fcAvailable: parseFloat(d.fcAllocated),
          bank: d.bank, currency: d.currency
        });
      });

      setAvailableForwards(fwds);
      setAvailablePcfc(pcfc);
      setForwardAllocations(detFwd.map(d => ({
        forwardId: d.forwardId,
        fcAllocated: String(d.fcAllocated),
        forwardRate: d.forwardRate
      })));
      setPcfcAllocations(detPcfc.map(d => ({
        pcfcId: d.pcfcId,
        fcAllocated: String(d.fcAllocated)
      })));

      setForm({
        customerCode:       r.customerCode || '',
        invoiceId:          r.invoiceId ? String(r.invoiceId) : '',
        discountingDate:    ddate,
        bank:               r.bank || '',
        fcAmountDiscounted: r.fcAmountDiscounted != null ? String(r.fcAmountDiscounted) : '',
        discountingType:    r.discountingType || 'INR',
        spotFc:             r.spotFc != null && parseFloat(r.spotFc) > 0 ? String(r.spotFc) : '',
        spotRate:           r.spotRate != null && parseFloat(r.spotRate) > 0 ? String(r.spotRate) : '',
        pcfcFc:             '', pcfcRate: '',
        drawdownRate:       r.drawdownRate != null && parseFloat(r.drawdownRate) > 0 ? String(r.drawdownRate) : '',
        loanDueDate:        r.loanDueDate ? String(r.loanDueDate).slice(0, 10) : '',
        notes:              typeof r.notes === 'string' ? r.notes : ''
      });

      setEditingRecord(r);
      setShowForm(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error(err);
      alert('Could not load this record for editing.');
    }
  };

  // Calculate totals for balance bar
  const calcTotals = () => {
    const fcToDiscount   = parseFloat(form.fcAmountDiscounted || 0);
    const spotFc         = parseFloat(form.spotFc   || 0);
    const forwardFc      = forwardAllocations
      .reduce((s, a) => s + parseFloat(a.fcAllocated || 0), 0);
    const pcfcFc         = pcfcAllocations
      .reduce((s, a) => s + parseFloat(a.fcAllocated || 0), 0);
    const totalAllocated = spotFc + forwardFc + pcfcFc;
    const remaining      = fcToDiscount - totalAllocated;

    let weightedSum = 0;
    let totalFc     = 0;
    if (spotFc > 0 && form.spotRate) {
      weightedSum += spotFc * parseFloat(form.spotRate);
      totalFc     += spotFc;
    }
    forwardAllocations.forEach(a => {
      const fc = parseFloat(a.fcAllocated || 0);
      if (fc > 0) {
        weightedSum += fc * parseFloat(a.forwardRate || 0);
        totalFc     += fc;
      }
    });
    // PCFC valued at each loan's draw rate (auto-filled)
    pcfcAllocations.forEach(a => {
      const fc = parseFloat(a.fcAllocated || 0);
      if (fc > 0) {
        const loan = availablePcfc.find(p => p.pcfcId === a.pcfcId);
        const rate = loan ? parseFloat(loan.drawdownRate || 0) : 0;
        weightedSum += fc * rate;
        totalFc     += fc;
      }
    });
    const weightedAvg = totalFc > 0 ? weightedSum / totalFc : 0;

    return { fcToDiscount, totalAllocated, remaining, weightedAvg };
  };

  const handleSubmit = async () => {
    // New / Edit — both go through the full validation and payload below.
    if (!form.customerCode)       { alert('Please select a customer'); return; }
    if (!form.invoiceId)          { alert('Please select an invoice'); return; }
    if (!form.discountingDate)    { alert('Please enter discounting date'); return; }
    if (!form.bank)               { alert('Please enter bank name'); return; }
    if (!form.fcAmountDiscounted) { alert('Please enter FC amount to discount'); return; }

    const { remaining } = calcTotals();

    // If a spot amount is entered, a spot rate is required (both types)
    if (parseFloat(form.spotFc || 0) > 0 && !form.spotRate) {
      alert('Please enter the spot rate'); return;
    }

    if (form.discountingType === 'INR') {
      if (Math.abs(remaining) > 0.01) {
        alert(`Balance not zero. Remaining FC to allocate: ${remaining.toFixed(2)}`);
        return;
      }
    } else {
      if (!form.loanDueDate)  { alert('Please enter loan due date'); return; }
      if (Math.abs(remaining) > 0.01) {
        alert(`Balance not zero. Spot + Forward + PCFC must equal the FC discounted. Remaining: ${remaining.toFixed(2)}`);
        return;
      }
    }

    const payload = {
      invoiceId:          parseInt(form.invoiceId),
      discountingDate:    form.discountingDate,
      bank:               form.bank,
      fcAmountDiscounted: parseFloat(form.fcAmountDiscounted),
      discountingType:    form.discountingType,
      spotFc:             form.spotFc      ? parseFloat(form.spotFc)      : null,
      spotRate:           form.spotRate    ? parseFloat(form.spotRate)    : null,
      pcfcFc:             form.pcfcFc      ? parseFloat(form.pcfcFc)      : null,
      pcfcRate:           form.pcfcRate    ? parseFloat(form.pcfcRate)    : null,
      drawdownRate:       form.drawdownRate ? parseFloat(form.drawdownRate) : null,
      loanDueDate:        form.loanDueDate  || null,
      notes:              form.notes        || null,
      forwardAllocations: forwardAllocations
        .filter(a => parseFloat(a.fcAllocated || 0) > 0)
        .map(a => ({
          forwardId:   a.forwardId,
          fcAllocated: parseFloat(a.fcAllocated)
        })),
      pcfcAllocations: pcfcAllocations
        .filter(a => parseFloat(a.fcAllocated || 0) > 0)
        .map(a => ({
          pcfcId:      a.pcfcId,
          fcAllocated: parseFloat(a.fcAllocated)
        }))
    };

    try {
      if (editingRecord) {
        await axios.put(
          `${CONFIG.API}/api/bills/discounting/${editingRecord.discountingId}`,
          payload, { headers: { authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${CONFIG.API}/api/bills/discounting`, payload, {
          headers: { authorization: `Bearer ${token}` }
        });
      }
      resetForm();
      loadRecords();
      loadCustomers();
    } catch (err) {
      const msg = err?.response?.data || 'Please check all fields.';
      alert('Error saving discounting record. ' + msg);
    }
  };

  const getStatusBadge = (status) =>
    status === 'REALISED' ? 'badge-done' : 'badge-open';

  const selectedInvoice = openInvoices.find(
    i => String(i.invoiceId) === String(form.invoiceId)
  );

  const { fcToDiscount, totalAllocated, remaining, weightedAvg } = calcTotals();

  const filteredRecords = records.filter(r => {
    const matchStatus   = filterStatus   === '' || r.realisationStatus === filterStatus;
    const matchCustomer = filterCustomer === '' ||
      r.customerCode.toLowerCase().includes(filterCustomer.toLowerCase());
    return matchStatus && matchCustomer;
  });

  return (
    <div className="content">

      {/* Edit Warning Popup */}
      {showEditWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '28px',
            maxWidth: '460px', width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <div style={{ fontSize: '18px', fontWeight: '700',
                          color: '#1E293B', marginBottom: '12px' }}>
              ⚠️ Edit Discounting Record
            </div>
            <div style={{ fontSize: '14px', color: '#475569',
                          lineHeight: '1.6', marginBottom: '20px' }}>
              You can only edit:
              <ul style={{ marginTop: '8px', paddingLeft: '18px' }}>
                <li>Discounting date</li>
                <li>Bank</li>
                <li>Notes</li>
              </ul>
              If you need to change the FC amount, allocations or rates —
              please contact support to reverse and recreate the entry.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-secondary"
                onClick={() => { setShowEditWarning(false); setPendingEditRecord(null); }}>
                Close
              </button>
              <button className="btn-primary" onClick={handleEditConfirm}>
                Yes, Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Total discounting</div>
          <div className="metric-value val-blue">{records.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Open</div>
          <div className="metric-value val-amber">
            {records.filter(r => r.realisationStatus === 'OPEN').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Realised</div>
          <div className="metric-value val-green">
            {records.filter(r => r.realisationStatus === 'REALISED').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">INR type</div>
          <div className="metric-value val-blue">
            {records.filter(r => r.discountingType === 'INR').length}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="section-header">
        <div className="section-title">Discounting records</div>
        <button className="btn-primary" onClick={() => {
          if (showForm) { resetForm(); } else { setShowForm(true); }
        }}>
          {showForm ? '✕ Close' : '+ New discounting'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="form-card" style={{ maxWidth: '760px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingRecord ? '✏️ Edit discounting record' : '📋 New discounting entry'}
          </div>

          {(
            <>
              {/* Row 1 — Customer + Invoice */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                            gap: '12px', marginBottom: '12px' }}>
                <div className="form-field">
                  <label>Customer code</label>
                  <select name="customerCode" value={form.customerCode}
                    onChange={handleChange} disabled={!!editingRecord}>
                    <option value="">— Select customer —</option>
                    {customers.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Invoice</label>
                  <select name="invoiceId" value={form.invoiceId}
                    onChange={handleChange} disabled={!!editingRecord}>
                    <option value="">— Select invoice —</option>
                    {openInvoices.map(inv => (
                      <option key={inv.invoiceId} value={inv.invoiceId}>
                        {inv.invoiceNo} — FC open: {Number(inv.fcOpen).toLocaleString()} — Due: {inv.dueDate}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2 — Date, Bank, FC amount, Type */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
                            gap: '12px', marginBottom: '12px' }}>
                <div className="form-field">
                  <label>Discounting date</label>
                  <input type="date" name="discountingDate"
                    value={form.discountingDate} onChange={handleChange} />
                </div>
                <div className="form-field">
                  <label>Bank</label>
                  <select name="bank" value={form.bank} onChange={handleChange}>
                    <option value="">— Select bank —</option>
                    {form.bank && !banks.some(b => b.bankName === form.bank) && (
                      <option value={form.bank}>{form.bank} (old)</option>
                    )}
                    {banks.map(b => (
                      <option key={b.bankId} value={b.bankName}>
                        {b.bankName}{b.branch ? ' — ' + b.branch : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>FC amount to discount</label>
                  <input type="number" name="fcAmountDiscounted"
                    value={form.fcAmountDiscounted} onChange={handleChange}
                    placeholder="0"
                    max={selectedInvoice?.fcOpen || ''} />
                </div>
                <div className="form-field">
                  <label>Discounting type</label>
                  <select name="discountingType" value={form.discountingType}
                    onChange={handleChange}>
                    <option value="INR">INR</option>
                    <option value="FC">FC (EBR)</option>
                  </select>
                </div>
              </div>

              {/* Invoice details bar */}
              {selectedInvoice && (
                <div style={{
                  background: '#F0F9FF', border: '1px solid #BAE6FD',
                  borderRadius: '6px', padding: '10px 14px',
                  fontSize: '13px', marginBottom: '14px',
                  display: 'flex', gap: '24px', flexWrap: 'wrap'
                }}>
                  <span><strong>FC open:</strong> {Number(selectedInvoice.fcOpen).toLocaleString()}</span>
                  <span><strong>Currency:</strong> {selectedInvoice.currency}</span>
                  <span><strong>Due date:</strong> {selectedInvoice.dueDate}</span>
                  <span><strong>Cost rate:</strong> {Number(selectedInvoice.weightedAvgCostRate).toFixed(4)}</span>
                </div>
              )}

              {/* INR Discounting Section */}
              {form.discountingType === 'INR' && form.invoiceId && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600',
                                color: '#374151', marginBottom: '10px' }}>
                    Allocation — Spot / Forward
                  </div>

                  {/* Spot row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                                gap: '12px', marginBottom: '6px' }}>
                    <div className="form-field">
                      <label>Spot — FC amount</label>
                      <input type="number" name="spotFc" value={form.spotFc}
                        onChange={handleChange} placeholder="0" />
                    </div>
                    <div className="form-field">
                      <label>Spot rate</label>
                      <input type="number" name="spotRate" value={form.spotRate}
                        onChange={handleChange} placeholder="e.g. 84.50" />
                    </div>
                  </div>
                  {form.spotFc && form.spotRate && (
                    <div style={{ fontSize: '12px', color: '#64748B',
                                  marginBottom: '8px', paddingLeft: '4px' }}>
                      Spot INR = {Number(parseFloat(form.spotFc) *
                        parseFloat(form.spotRate)).toLocaleString('en-IN',
                        { maximumFractionDigits: 0 })}
                    </div>
                  )}

                  {/* Forward allocation table */}
                  {availableForwards.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600',
                                    color: '#374151', marginBottom: '6px' }}>
                        Forward contracts matching invoice due date
                      </div>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Deal no</th>
                            <th>Bank</th>
                            <th>Forward rate</th>
                            <th>FC available</th>
                            <th>Window</th>
                            <th>Allocate FC</th>
                            <th>INR value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {availableForwards.map(fwd => {
                            const alloc = forwardAllocations.find(
                              a => a.forwardId === fwd.forwardId);
                            const allocFc = parseFloat(alloc?.fcAllocated || 0);
                            const inrVal  = allocFc * parseFloat(fwd.forwardRate);
                            return (
                              <tr key={fwd.forwardId}>
                                <td>{fwd.dealNo}</td>
                                <td>{fwd.bank}</td>
                                <td>{Number(fwd.forwardRate).toFixed(4)}</td>
                                <td>{Number(fwd.fcAvailable).toLocaleString()}</td>
                                <td style={{ fontSize: '11px' }}>
                                  {fwd.windowStartDate} to {fwd.windowEndDate}
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={alloc?.fcAllocated || ''}
                                    onChange={e => handleForwardAllocationChange(
                                      fwd.forwardId, e.target.value)}
                                    placeholder="0"
                                    max={fwd.fcAvailable}
                                    style={{
                                      width: '100px', padding: '4px 8px',
                                      border: '1px solid #CBD5E1',
                                      borderRadius: '4px', fontSize: '13px'
                                    }}
                                  />
                                </td>
                                <td style={{ color: '#166534', fontWeight: '600' }}>
                                  {allocFc > 0
                                    ? Number(inrVal).toLocaleString('en-IN',
                                        { maximumFractionDigits: 0 })
                                    : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {form.invoiceId && availableForwards.length === 0 && (
                    <div style={{
                      background: '#FEF9C3', border: '1px solid #FDE68A',
                      borderRadius: '6px', padding: '8px 12px',
                      fontSize: '12px', color: '#92400E', marginBottom: '10px'
                    }}>
                      No forward contracts found matching invoice due date.
                    </div>
                  )}

                  {/* Balance bar */}
                  {fcToDiscount > 0 && (
                    <div style={{
                      background: Math.abs(remaining) < 0.01 ? '#F0FDF4' : '#FEF2F2',
                      border: `1px solid ${Math.abs(remaining) < 0.01 ? '#BBF7D0' : '#FCA5A5'}`,
                      borderRadius: '6px', padding: '10px 14px',
                      fontSize: '13px', marginTop: '8px'
                    }}>
                      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                        <span><strong>FC to discount:</strong> {Number(fcToDiscount).toLocaleString()}</span>
                        <span><strong>Total allocated:</strong> {Number(totalAllocated).toLocaleString()}</span>
                        <span style={{
                          color: Math.abs(remaining) < 0.01 ? '#166534' : '#DC2626',
                          fontWeight: '700'
                        }}>
                          <strong>Remaining:</strong> {Number(remaining).toLocaleString()}
                        </span>
                        {weightedAvg > 0 && (
                          <span style={{ color: '#1D4ED8', fontWeight: '600' }}>
                            <strong>Wtd avg rate:</strong> {weightedAvg.toFixed(4)}
                          </span>
                        )}
                        {weightedAvg > 0 && selectedInvoice?.weightedAvgCostRate > 0 && (
                          <span style={{
                            color: weightedAvg >= selectedInvoice.weightedAvgCostRate
                              ? '#166534' : '#DC2626',
                            fontWeight: '600'
                          }}>
                            <strong>Ex diff:</strong> ₹{Number(
                              (weightedAvg - parseFloat(selectedInvoice.weightedAvgCostRate))
                              * fcToDiscount
                            ).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* FC Discounting Section */}
              {form.discountingType === 'FC' && form.invoiceId && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                                gap: '12px', marginBottom: '12px' }}>
                    <div className="form-field">
                      <label>Loan due date</label>
                      <input type="date" name="loanDueDate"
                        value={form.loanDueDate} onChange={handleChange} />
                    </div>
                  </div>

                  {/* Spot allocation */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                                gap: '12px', marginBottom: '6px' }}>
                    <div className="form-field">
                      <label>Spot — FC amount</label>
                      <input type="number" name="spotFc" value={form.spotFc}
                        onChange={handleChange} placeholder="0" />
                    </div>
                    <div className="form-field">
                      <label>Spot rate</label>
                      <input type="number" name="spotRate" value={form.spotRate}
                        onChange={handleChange} placeholder="e.g. 84.50" />
                    </div>
                  </div>
                  {form.spotFc && form.spotRate && (
                    <div style={{ fontSize: '12px', color: '#64748B',
                                  marginBottom: '8px', paddingLeft: '4px' }}>
                      Spot INR = {Number(parseFloat(form.spotFc) *
                        parseFloat(form.spotRate)).toLocaleString('en-IN',
                        { maximumFractionDigits: 0 })}
                    </div>
                  )}

                  {availableForwards.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600',
                                    color: '#374151', marginBottom: '6px' }}>
                        Forward contracts matching discounting date
                      </div>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Deal no</th>
                            <th>Bank</th>
                            <th>Forward rate</th>
                            <th>FC available</th>
                            <th>Window</th>
                            <th>Allocate FC</th>
                          </tr>
                        </thead>
                        <tbody>
                          {availableForwards.map(fwd => {
                            const alloc = forwardAllocations.find(
                              a => a.forwardId === fwd.forwardId);
                            return (
                              <tr key={fwd.forwardId}>
                                <td>{fwd.dealNo}</td>
                                <td>{fwd.bank}</td>
                                <td>{Number(fwd.forwardRate).toFixed(4)}</td>
                                <td>{Number(fwd.fcAvailable).toLocaleString()}</td>
                                <td style={{ fontSize: '11px' }}>
                                  {fwd.windowStartDate} to {fwd.windowEndDate}
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={alloc?.fcAllocated || ''}
                                    onChange={e => handleForwardAllocationChange(
                                      fwd.forwardId, e.target.value)}
                                    placeholder="0"
                                    max={fwd.fcAvailable}
                                    style={{
                                      width: '100px', padding: '4px 8px',
                                      border: '1px solid #CBD5E1',
                                      borderRadius: '4px', fontSize: '13px'
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* PCFC repayment — FC discounting only */}
                  <div style={{ marginTop: '14px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600',
                                  color: '#374151', marginBottom: '8px' }}>
                      PCFC repayment (optional) — oldest loans first
                    </div>

                    {!form.bank && (
                      <div style={{
                        background: '#FEF9C3', border: '1px solid #FDE68A',
                        borderRadius: '6px', padding: '8px 12px',
                        fontSize: '12px', color: '#92400E', marginBottom: '8px'
                      }}>
                        Select a bank above to see its PCFC loans.
                      </div>
                    )}

                    {form.bank && availablePcfc.length === 0 && (
                      <div style={{
                        background: '#FEF9C3', border: '1px solid #FDE68A',
                        borderRadius: '6px', padding: '8px 12px',
                        fontSize: '12px', color: '#92400E', marginBottom: '8px'
                      }}>
                        No open PCFC loans for this bank and currency.
                      </div>
                    )}

                    {availablePcfc.length > 0 && (
                      <>
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>PCFC ref</th>
                              <th>Date</th>
                              <th>Draw rate</th>
                              <th>FC outstanding</th>
                              <th>Repay FC</th>
                            </tr>
                          </thead>
                          <tbody>
                            {availablePcfc.map(p => {
                              const alloc = pcfcAllocations.find(
                                a => a.pcfcId === p.pcfcId);
                              return (
                                <tr key={p.pcfcId}>
                                  <td>{p.pcfcRef}</td>
                                  <td>{p.pcfcDate}</td>
                                  <td>{Number(p.drawdownRate).toFixed(4)}</td>
                                  <td>{Number(p.fcAvailable).toLocaleString()}</td>
                                  <td>
                                    <input
                                      type="number"
                                      value={alloc?.fcAllocated || ''}
                                      onChange={e => handlePcfcAllocationChange(
                                        p.pcfcId, e.target.value)}
                                      placeholder="0"
                                      max={p.fcAvailable}
                                      style={{
                                        width: '100px', padding: '4px 8px',
                                        border: '1px solid #CBD5E1',
                                        borderRadius: '4px', fontSize: '13px'
                                      }}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                      </>
                    )}
                  </div>

                  {/* FC balance + weighted average realisation rate */}
                  {fcToDiscount > 0 && (
                    <div style={{
                      background: Math.abs(remaining) < 0.01 ? '#F0FDF4' : '#FEF2F2',
                      border: `1px solid ${Math.abs(remaining) < 0.01 ? '#BBF7D0' : '#FCA5A5'}`,
                      borderRadius: '6px', padding: '10px 14px',
                      fontSize: '13px', marginTop: '8px'
                    }}>
                      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                        <span><strong>FC to discount:</strong> {Number(fcToDiscount).toLocaleString()}</span>
                        <span><strong>Total allocated:</strong> {Number(totalAllocated).toLocaleString()}</span>
                        <span style={{
                          color: Math.abs(remaining) < 0.01 ? '#166534' : '#DC2626',
                          fontWeight: '700'
                        }}>
                          <strong>Remaining:</strong> {Number(remaining).toLocaleString()}
                        </span>
                        {weightedAvg > 0 && (
                          <span style={{ color: '#1D4ED8', fontWeight: '600' }}>
                            <strong>Wtd avg realisation rate:</strong> {weightedAvg.toFixed(4)}
                          </span>
                        )}
                        {weightedAvg > 0 && selectedInvoice?.weightedAvgCostRate > 0 && (
                          <span style={{
                            color: weightedAvg >= selectedInvoice.weightedAvgCostRate
                              ? '#166534' : '#DC2626',
                            fontWeight: '600'
                          }}>
                            <strong>Ex diff (cost):</strong> ₹{Number(
                              (weightedAvg - parseFloat(selectedInvoice.weightedAvgCostRate))
                              * fcToDiscount
                            ).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div style={{ marginBottom: '16px' }}>
                <div className="form-field">
                  <label>Notes (optional)</label>
                  <textarea name="notes" value={form.notes}
                    onChange={handleChange} rows={2}
                    placeholder="Any additional notes..."
                    style={{
                      width: '100%', padding: '8px 10px',
                      border: '1px solid #CBD5E1', borderRadius: '6px',
                      fontSize: '14px', resize: 'vertical',
                      fontFamily: 'inherit', boxSizing: 'border-box'
                    }} />
                </div>
              </div>
            </>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetForm}>Cancel</button>
            <button className="btn-primary" onClick={handleSubmit}>
              {editingRecord ? '✓ Update record' : '✓ Save discounting'}
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: '12px', alignItems: 'center',
        marginBottom: '12px', flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="🔍 Search customer..."
          value={filterCustomer}
          onChange={e => setFilterCustomer(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', width: '200px'
          }}
        />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', color: '#374151'
          }}
        >
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="REALISED">Realised</option>
        </select>
        {(filterStatus !== '' || filterCustomer !== '') && (
          <button
            onClick={() => { setFilterStatus(''); setFilterCustomer(''); }}
            style={{
              padding: '7px 12px', border: '1px solid #CBD5E1',
              borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
              background: '#F1F5F9', color: '#64748B'
            }}>
            ✕ Clear filters
          </button>
        )}
        <span style={{ fontSize: '13px', color: '#64748B', marginLeft: 'auto' }}>
          Showing {filteredRecords.length} of {records.length} records
        </span>
      </div>

      {/* Records table */}
      <div className="table-wrap">
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : filteredRecords.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No discounting records found.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice no</th>
                <th>Customer</th>
                <th>Currency</th>
                <th>Disc date</th>
                <th>Bank</th>
                <th>FC discounted</th>
                <th>Type</th>
                <th>Wtd avg rate</th>
                <th>Cost rate</th>
                <th>Ex diff ₹</th>
                <th>Due date</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map(r => (
                <tr key={r.discountingId}>
                  <td>{r.invoiceNo}</td>
                  <td>{r.customerCode}</td>
                  <td>{r.currency}</td>
                  <td>{r.discountingDate}</td>
                  <td>{r.bank}</td>
                  <td>{Number(r.fcAmountDiscounted).toLocaleString()}</td>
                  <td>{r.discountingType}</td>
                  <td>{r.weightedAvgRate > 0
                    ? Number(r.weightedAvgRate).toFixed(4) : '—'}</td>
                  <td>{r.weightedAvgCostRate > 0
                    ? Number(r.weightedAvgCostRate).toFixed(4) : '—'}</td>
                  <td style={{
                    color: r.exchangeDiff >= 0 ? '#166534' : '#DC2626',
                    fontWeight: '600'
                  }}>
                    {r.weightedAvgRate > 0
                      ? Number(r.exchangeDiff).toLocaleString('en-IN',
                          { maximumFractionDigits: 0 })
                      : '—'}
                  </td>
                  <td>{r.dueDate}</td>
                  <td>
                    <span className={`badge ${getStatusBadge(r.realisationStatus)}`}>
                      {r.realisationStatus}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {r.realisationStatus === 'OPEN' ? (
                      <button
                        className="btn-secondary"
                        onClick={() => handleEditClick(r)}>
                        Edit
                      </button>
                    ) : (
                      <span
                        title="This bill is realised. Delete the realisation first to edit the discounting."
                        style={{ fontSize: '12px', color: '#92400E' }}>
                        🔒 Realised — delete realisation to edit
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

export default DiscountingPage;